import React, { useMemo } from 'react';
import { formatNumEU } from '../dummyData';
import { HiddenValue, useValueHide } from '../ValueHideContext';
import { SubscriptionSummary } from '../../../../types/subscription';

const EXCHANGE_COLORS: Record<string, string> = {
  BINANCE: '#F0B90B',
  KUCOIN: '#22B573',
  MEXC: '#1C6CF1',
  COINBASE: '#0052FF',
  KRAKEN: '#5741D9',
  PHEMEX: '#D4A017',
  BYBIT: '#F7A600',
};

const EXCHANGE_NAMES: Record<string, string> = {
  BINANCE: 'Binance',
  KUCOIN: 'KuCoin',
  MEXC: 'MEXC',
  COINBASE: 'Coinbase',
  KRAKEN: 'Kraken',
  PHEMEX: 'Phemex',
  BYBIT: 'Bybit',
};

// ── Donut SVG ────────────────────────────────────────────────────────────────

interface ExchangeData {
  name: string;
  value: number;
  color: string;
}

interface DonutProps {
  data: ExchangeData[];
  size?: number;
  thickness?: number;
  hovered: number | null;
  onHover: (idx: number | null) => void;
  valuesHidden: boolean;
}

const Donut: React.FC<DonutProps> = ({
  data,
  size = 180,
  thickness = 20,
  hovered,
  onHover,
  valuesHidden,
}) => {
  const total = data.reduce((a, b) => a + Math.abs(b.value), 0);
  const r = size / 2 - thickness / 2 - 2;
  const cx = size / 2;
  const cy = size / 2;

  let acc = 0;
  const slices = data.map((d, i) => {
    const v = Math.abs(d.value);
    const a0 = (acc / total) * Math.PI * 2 - Math.PI / 2;
    acc += v;
    const a1 = (acc / total) * Math.PI * 2 - Math.PI / 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + Math.cos(a0) * r;
    const y0 = cy + Math.sin(a0) * r;
    const x1 = cx + Math.cos(a1) * r;
    const y1 = cy + Math.sin(a1) * r;
    return {
      d,
      path: `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1}`,
      idx: i,
    };
  });

  const centerLabel = hovered !== null ? data[hovered].name : 'Total';
  const centerValue = valuesHidden
    ? '••••'
    : hovered !== null
    ? '$' + formatNumEU(data[hovered].value).split(',')[0]
    : '$' + formatNumEU(total).split(',')[0];

  return (
    <svg width={size} height={size} style={{ overflow: 'visible' }}>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#222"
        strokeWidth={thickness}
      />
      {slices.map((s) => (
        <path
          key={s.idx}
          d={s.path}
          fill="none"
          stroke={s.d.color}
          strokeWidth={thickness}
          strokeLinecap="butt"
          opacity={hovered === null ? 1 : hovered === s.idx ? 1 : 0.25}
          style={{ transition: 'opacity 0.15s', cursor: 'pointer' }}
          onMouseEnter={() => onHover(s.idx)}
          onMouseLeave={() => onHover(null)}
        />
      ))}
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        fill="#808080"
        fontSize={13}
        fontFamily="Kanit, sans-serif"
      >
        {centerLabel}
      </text>
      <text
        x={cx}
        y={cy + 16}
        textAnchor="middle"
        fill="#fff"
        fontSize={22}
        fontWeight={600}
        fontFamily="Kanit, sans-serif"
      >
        {centerValue}
      </text>
    </svg>
  );
};

// ── ExchangeAllocation ────────────────────────────────────────────────────────

interface ExchangeAllocationProps {
  subscriptions: SubscriptionSummary[];
}

const ExchangeAllocation: React.FC<ExchangeAllocationProps> = ({
  subscriptions,
}) => {
  const [hovered, setHovered] = React.useState<number | null>(null);
  const { hidden: valuesHidden } = useValueHide();

  const exchangeData = useMemo(() => {
    const byExchange: Record<string, number> = {};
    subscriptions.forEach((sub) => {
      const key = sub.exchange;
      byExchange[key] = (byExchange[key] || 0) + sub.totalBalanceInStable;
    });

    return Object.entries(byExchange)
      .map(([exchange, value]) => ({
        name: EXCHANGE_NAMES[exchange] || exchange,
        value,
        color: EXCHANGE_COLORS[exchange] || '#808080',
      }))
      .sort((a, b) => b.value - a.value);
  }, [subscriptions]);

  const total = exchangeData.reduce((a, b) => a + b.value, 0);

  return (
    <div className="bottom-card">
      <span className="bottom-card-title">Allocation by Exchange</span>

      <div className="ea-donut-wrapper">
        <Donut
          data={exchangeData}
          size={180}
          thickness={20}
          hovered={hovered}
          onHover={setHovered}
          valuesHidden={valuesHidden}
        />

        <div className="ea-legend">
          {exchangeData.map((ex, i) => {
            const pct =
              total > 0 ? ((ex.value / total) * 100).toFixed(1) : '0.0';
            return (
              <div
                key={ex.name}
                className="ea-legend-row"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'default' }}
              >
                <div className="ea-legend-left">
                  <span
                    className="ea-legend-dot"
                    style={{ backgroundColor: ex.color }}
                  />
                  <span className="ea-legend-name">{ex.name}</span>
                </div>
                <div className="ea-legend-right">
                  <span className="ea-legend-value">
                    <HiddenValue width={60}>
                      ${formatNumEU(ex.value)}
                    </HiddenValue>
                  </span>
                  <span className="ea-legend-pct">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExchangeAllocation;
